// frontend/src/CoachLineups.js
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { apiFetch } from "./api";
import UnassignedRosterPanel from "./UnassignedRosterPanel";
import PublishLineupModal from "./PublishLineupModal";
import { FaCheckCircle, FaQuestionCircle, FaExclamationTriangle, FaClipboardCheck, FaChevronDown, FaChevronUp, FaChevronLeft, FaEllipsisH, FaFilter, FaUserPlus } from "react-icons/fa";
import { FaClipboardQuestion } from "react-icons/fa6";
import { formatPracticeTimeRange } from "./utils";

const HULL_SIZE_BY_TYPE = {
  eight: "8",
  four: "4",
  pair: "2",
};

const ASSET_STATUS_COLORS = {
  good: { background: "rgba(52,211,153,0.15)", border: "rgba(52,211,153,0.4)", text: "var(--success)" },
  warn: { background: "rgba(251,191,36,0.15)", border: "rgba(251,191,36,0.4)", text: "var(--warn)" },
  bad: { background: "rgba(248,113,113,0.15)", border: "rgba(248,113,113,0.4)", text: "var(--danger)" },
  neutral: { background: "var(--bg-elevated)", border: "var(--border)", text: "var(--muted)" },
};

const ASSET_CHIP_BASE_STYLE = {
  cursor: "pointer",
  padding: "4px 8px",
  fontSize: "12px",
  borderRadius: "4px",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  gap: 6,
};

function AbsentStatusIcon({ size = 14 }) {
  return (
    <span className="statusIcon absent custom-absent" style={{ width: size, height: size }}>
      <span className="absent-icon-inner">×</span>
    </span>
  );
}

function normalizeStatusName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function getAssetStatusVariant(statusName) {
  const normalized = normalizeStatusName(statusName);
  if (normalized === "unusable") return "bad";
  if (normalized === "in repairs" || normalized === "in repair" || normalized === "usable") return "warn";
  if (normalized === "good" || normalized === "") return "good";
  return "neutral";
}

export default function CoachLineups({ practiceId: practiceIdProp, date, roster, coachTeam, coach }) {
  const canEditLineups =
    coach?.permissions?.includes?.("lineups:edit") ?? coach?.can_edit_lineup ?? false;
  const [boats, setBoats] = useState([]);
  const [type, setType] = useState("eight");
  const [name, setName] = useState(""); // keep name support (hidden, but used in addBoat)
  const [hull, setHull] = useState("");
  const [boatCoach, setBoatCoach] = useState("");
  const [oars, setOars] = useState("");

  // Asset modal state for showing maintenance tasks
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetTasks, setAssetTasks] = useState([]);

  // Track which assets have open maintenance tasks
  const [assetsWithTasks, setAssetsWithTasks] = useState(new Set());

  // Boat configuration picklists
  const [boatConfig, setBoatConfig] = useState({ hulls: [], oars: [], coxBoxes: [], launches: [], speedCoaches: [], coaches: [] });

  // Position colors from config
  const [positionColors, setPositionColors] = useState({});
  const [configuredSquads, setConfiguredSquads] = useState([]);
  const [configuredPositions, setConfiguredPositions] = useState([]);


  const getAssetListByType = useCallback((assetType) => {
    if (assetType === "hull") return boatConfig.hulls;
    if (assetType === "oars") return boatConfig.oars;
    if (assetType === "cox_boxes") return boatConfig.coxBoxes;
    if (assetType === "launch") return boatConfig.launches;
    if (assetType === "speed_coach") return boatConfig.speedCoaches;
    return [];
  }, [boatConfig]);

  const findAssetDetails = useCallback((assetValue, assetType) => {
    if (!assetValue) return null;
    const list = getAssetListByType(assetType);
    return list.find((item) => item.value === assetValue) || null;
  }, [getAssetListByType]);

  const getAssetStatusInfo = useCallback((assetValue, assetType) => {
    if (!assetValue) {
      return {
        label: "No Status",
        styles: ASSET_STATUS_COLORS.good,
      };
    }
    const match = findAssetDetails(assetValue, assetType);
    const statusName = match?.statusName?.trim() || "";
    const variant = getAssetStatusVariant(statusName);
    const styles = ASSET_STATUS_COLORS[variant] || ASSET_STATUS_COLORS.neutral;
    return {
      label: statusName || "No Status",
      styles,
    };
  }, [findAssetDetails]);

  const selectedAssetDetails = useMemo(() => {
    if (!selectedAsset) return null;
    return findAssetDetails(selectedAsset.name, selectedAsset.type);
  }, [selectedAsset, findAssetDetails]);
  const selectedAssetStatusInfo = useMemo(() => {
    if (!selectedAsset) return null;
    return getAssetStatusInfo(selectedAsset.name, selectedAsset.type);
  }, [selectedAsset, getAssetStatusInfo]);
  const selectedAssetProperties = useMemo(() => {
    if (!selectedAsset || !selectedAssetDetails) return [];
    const rows = [];
    if (selectedAssetDetails.teamName) rows.push({ label: "Team", value: selectedAssetDetails.teamName });
    if (selectedAssetDetails.squadName) rows.push({ label: "Squad", value: selectedAssetDetails.squadName });
    if (selectedAsset.type === "hull") {
      if (selectedAssetDetails.size) rows.push({ label: "Hull Size", value: selectedAssetDetails.size });
      if (selectedAssetDetails.rig) rows.push({ label: "Rig", value: selectedAssetDetails.rig });
      if (selectedAssetDetails.spread) rows.push({ label: "Spread", value: selectedAssetDetails.spread });
    }
    if (selectedAsset.type === "oars") {
      if (selectedAssetDetails.oarLength) rows.push({ label: "Oar Length", value: selectedAssetDetails.oarLength });
      if (selectedAssetDetails.oarInboard) rows.push({ label: "Inboard", value: selectedAssetDetails.oarInboard });
    }
    if (selectedAsset.type === "launch") {
      if (selectedAssetDetails.location) rows.push({ label: "Location", value: selectedAssetDetails.location });
    } else if (selectedAssetDetails.location) {
      rows.push({ label: "Location", value: selectedAssetDetails.location });
    }
    return rows;
  }, [selectedAsset, selectedAssetDetails]);
  const selectedAssetNotes = useMemo(() => {
    if (!selectedAssetDetails) return "";
    return selectedAssetDetails.notes ?? selectedAssetDetails.note ?? "";
  }, [selectedAssetDetails]);

  // ---- Teams (filter) ----
  // Teams list: dedupe case-insensitively, display original casing
  const normalizedCoachTeam = String(coachTeam || "").trim();

  const teams = useMemo(() => {
    const m = new Map();
    (roster||[]).forEach(r => {
      const raw = String(r.team||"").trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (!m.has(key)) m.set(key, raw);
    });
    if (normalizedCoachTeam) {
      const key = normalizedCoachTeam.toLowerCase();
      if (!m.has(key)) m.set(key, normalizedCoachTeam);
    }
    return Array.from(m.values()).sort((a,b)=>a.localeCompare(b));
  }, [roster, normalizedCoachTeam]);
  const [teamFilter, setTeamFilter] = useState(() => (normalizedCoachTeam || "all"));

  useEffect(() => {
    if (normalizedCoachTeam) {
      setTeamFilter((current) =>
        current === "all" || !current ? normalizedCoachTeam : current
      );
    }
  }, [normalizedCoachTeam]);

  // ---- Practice picker state ----
  const [dayPractices, setDayPractices] = useState([]);      // practices for this date
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const selectedPracticeIdRef = useRef(selectedPracticeId);
  selectedPracticeIdRef.current = selectedPracticeId;
  const effectivePracticeId = selectedPracticeId ?? practiceIdProp ?? null;

  // ---- Coach availability state ----
  const [coaches, setCoaches] = useState([]);
  const [lineupCheckins, setLineupCheckins] = useState({});
  const [publishLoading, setPublishLoading] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [publishedNote, setPublishedNote] = useState("");
  const [showPublishModal, setShowPublishModal] = useState(false);

  // NEW: ref for horizontal scroll snap
  const scrollerRef = useRef(null);
  const pinchStartDistRef = useRef(null);
  const pinchStartZoomRef = useRef(1);
  const lineupWrapperRef = useRef(null);
  // Mobile tap-to-assign
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isCompactLineup, setIsCompactLineup] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerCtx, setPickerCtx] = useState(null); // { boatId, seat, currentUserId }
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerTeam, setPickerTeam] = useState(() => coachTeam || "all");
  const [pickerSquad, setPickerSquad] = useState("all");
  const [pickerPosition, setPickerPosition] = useState("all");
  const [pickerAgeGroup, setPickerAgeGroup] = useState("all");
  const [pickerAvailability, setPickerAvailability] = useState("all");
  const [pickerShowFilters, setPickerShowFilters] = useState(false);
  const [pickerShowFiller, setPickerShowFiller] = useState(false);
  const [pickerFillerName, setPickerFillerName] = useState("");
  const [showMobileCoaches, setShowMobileCoaches] = useState(false);
  const [showMobileRoster, setShowMobileRoster] = useState(false);
  const [lineupZoom, setLineupZoom] = useState(1);
  const [coachPickerOpen, setCoachPickerOpen] = useState(false);
  const [coachPickerBoatId, setCoachPickerBoatId] = useState(null);
  const [coachPickerSearch, setCoachPickerSearch] = useState("");
  const [coachPickerStatus, setCoachPickerStatus] = useState("all");
  const [coachPanelCollapsed, setCoachPanelCollapsed] = useState(true);
  const [rosterPanelCollapsed, setRosterPanelCollapsed] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef(null);
  const seatSearchRef = useRef(null);
  const seatSearchInputRef = useRef(null);
  const [seatSearchCtx] = useState(null); // { boatId, seat }
  const [boatActionOpen, setBoatActionOpen] = useState(false);
  const [boatActionType, setBoatActionType] = useState("eight");
  const [templateActionOpen, setTemplateActionOpen] = useState(false);
  const [templateActionId, setTemplateActionId] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia ? window.matchMedia("(pointer: coarse)") : null;
    const update = () => {
      const matches = mq ? mq.matches : "ontouchstart" in window;
      setIsTouchDevice(Boolean(matches));
    };
    update();
    mq?.addEventListener?.("change", update);
    return () => mq?.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(max-width: 900px)");
    const handleChange = () => setIsCompactLineup(query.matches);
    handleChange();
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", handleChange);
      return () => query.removeEventListener("change", handleChange);
    }
    query.addListener(handleChange);
    return () => query.removeListener(handleChange);
  }, []);

  // Pinch-to-zoom on the lineup wrapper
  useEffect(() => {
    const el = lineupWrapperRef.current;
    if (!el) return;
    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchStartDistRef.current = Math.hypot(dx, dy);
        pinchStartZoomRef.current = lineupZoom;
      }
    };
    const onTouchMove = (e) => {
      if (e.touches.length === 2 && pinchStartDistRef.current !== null) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        const ratio = dist / pinchStartDistRef.current;
        const next = Math.min(1, Math.max(0.35, pinchStartZoomRef.current * ratio));
        setLineupZoom(next);
      }
    };
    const onTouchEnd = () => { pinchStartDistRef.current = null; };
    const onOrientationChange = () => setLineupZoom(1);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('orientationchange', onOrientationChange);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('orientationchange', onOrientationChange);
    };
  }, [lineupZoom]);

  // Tracks which `date` the current dayPractices/selectedPracticeId reflect. Used so a
  // date change doesn't briefly fall back to the previous date's (now-stale) practice
  // id before the new date's practices have loaded, and so dependent panels (boats,
  // coaches) know to keep showing a loading state rather than a "nothing here" empty
  // state while that resolution is in flight.
  const loadedDateRef = useRef(null);
  const [practicesReadyForDate, setPracticesReadyForDate] = useState(false);

  // Load practices for this date
  const loadDayPractices = useCallback(async () => {
    const isDateChange = loadedDateRef.current !== date;
    // Only show the "resolving" state for an actual date change — a team-filter-only
    // refresh already has valid boats/coaches on screen and shouldn't flicker.
    if (isDateChange) setPracticesReadyForDate(false);
    if (!date) { setDayPractices([]); setSelectedPracticeId(practiceIdProp ?? null); loadedDateRef.current = date; setPracticesReadyForDate(true); return; }
    try {
      let url = `/practices/range?from=${date}&to=${date}`;
      if (teamFilter && teamFilter !== "all") {
        url += `&team=${encodeURIComponent(teamFilter)}`;
      }
      // Fetch practices and publish status in parallel so we can pick the right practice
      const [rows, statusResult] = await Promise.all([
        apiFetch(url),
        (teamFilter && teamFilter !== "all")
          ? apiFetch(`/lineups/${date}/status?team=${encodeURIComponent(teamFilter)}`).catch(() => null)
          : Promise.resolve(null),
      ]);
      const list = (Array.isArray(rows) ? rows : []).map(r => {
        const startTime = r.startTime || r.start_time || r.time || null;
        const endTime = r.endTime || r.end_time || null;
        return {
          id: r.id,
          date: String(r.date || "").slice(0, 10),
          time: startTime ? String(startTime).slice(0, 5) : null,
          startTime: startTime ? String(startTime).slice(0, 5) : null,
          endTime: endTime ? String(endTime).slice(0, 5) : null,
          state: r.state || "on",
          type: r.type || null,
          team: r.team || null,
          squad: r.squad || null,
          note: r.note || "",
        };
      }).filter(isPracticeType);
      list.sort((a, b) => (a.startTime || a.time || "").localeCompare(b.startTime || b.time || ""));

      // Resolve the target practice id before touching state, so the date/practice
      // list/selection all commit together in one render instead of a null-then-
      // reselect sequence.
      // On a date change, ignore the previous date's selection entirely (it can't be
      // valid for the new date's practice list).
      const current = isDateChange ? (practiceIdProp ?? null) : (selectedPracticeIdRef.current ?? practiceIdProp ?? null);
      const publishedPracticeId = statusResult?.published ? (Number(statusResult.practiceId) || null) : null;
      let nextPracticeId;
      if (publishedPracticeId && list.some(p => Number(p.id) === publishedPracticeId)) {
        nextPracticeId = publishedPracticeId;
      } else if (current != null && list.some(p => Number(p.id) === Number(current))) {
        nextPracticeId = current;
      } else {
        nextPracticeId = list[0]?.id ?? null;
      }

      loadedDateRef.current = date;
      setDayPractices(list);
      setSelectedPracticeId(nextPracticeId);
      setPracticesReadyForDate(true);
    } catch (e) {
      console.error("loadDayPractices error:", e);
      loadedDateRef.current = date;
      setDayPractices([]);
      setSelectedPracticeId(practiceIdProp ?? null);
      setPracticesReadyForDate(true);
    }
  }, [date, practiceIdProp, teamFilter]);

  useEffect(() => { loadDayPractices(); }, [loadDayPractices]);

  // Load coaches with availability for the selected practice
  const loadCoaches = useCallback(async () => {
    if (!effectivePracticeId || !date) {
      setCoaches([]);
      return;
    }
    try {
      const rows = await apiFetch(`/attendance/coach/by-date?date=${date}&practice=${effectivePracticeId}`);
      const normalizeCoachStatus = (val) => {
        const s = typeof val === "string" ? val.toLowerCase() : "";
        if (s === "available" || s === "present") return "present";
        if (s === "not available" || s === "unavailable" || s === "absent") return "absent";
        return null;
      };
      const mapped = (Array.isArray(rows) ? rows : []).map((row) => ({
        ...row,
        status: normalizeCoachStatus(row.status),
      }));
      setCoaches(mapped);
    } catch (e) {
      console.error("loadCoaches error:", e);
      setCoaches([]);
    }
  }, [effectivePracticeId, date]);

  useEffect(() => { loadCoaches(); }, [loadCoaches]);

  // Load boat configuration (hulls, oars, coaches)
  useEffect(() => {
    async function loadBoatConfig() {
      try {
        const config = await apiFetch('/boat-config');
        setBoatConfig({
          hulls: Array.isArray(config?.assets?.hull) ? config.assets.hull : (Array.isArray(config?.hulls) ? config.hulls : []),
          oars: Array.isArray(config?.assets?.oars) ? config.assets.oars : (Array.isArray(config?.oars) ? config.oars : []),
          coxBoxes: Array.isArray(config?.assets?.cox_boxes) ? config.assets.cox_boxes : (Array.isArray(config?.coxBoxes) ? config.coxBoxes : []),
          launches: Array.isArray(config?.assets?.launch) ? config.assets.launch : (Array.isArray(config?.launches) ? config.launches : []),
          speedCoaches: Array.isArray(config?.assets?.speed_coach) ? config.assets.speed_coach : (Array.isArray(config?.speedCoaches) ? config.speedCoaches : []),
          coaches: Array.isArray(config?.coaches) ? config.coaches : [],
        });
      } catch (e) {
        console.error('Failed to load boat config:', e);
      }
    }
    loadBoatConfig();
  }, []);

  // Load all equipment tasks to identify assets with open tasks
  useEffect(() => {
    async function loadAllEquipmentTasks() {
      try {
        const data = await apiFetch('/equipment-tasks');
        const openTasks = data.filter(task => task.status !== 'completed');
        const assetKeys = new Set(
          openTasks.map(task => `${task.equipment_type}:${task.equipment_name}`)
        );
        setAssetsWithTasks(assetKeys);
      } catch (e) {
        console.error('Failed to load equipment tasks:', e);
      }
    }
    loadAllEquipmentTasks();
  }, []);

  // Load position colors
  useEffect(() => {
    async function loadPositionColors() {
      try {
        const positions = await apiFetch('/positions');
        const list = Array.isArray(positions) ? positions : [];
        const colorMap = {};
        const configured = new Set();
        list.forEach((p) => {
          if (p.name) {
            configured.add(p.name.trim());
            if (p.color) {
              colorMap[p.name.toLowerCase()] = p.color;
            }
          }
        });
        setPositionColors(colorMap);
        setConfiguredPositions(
          Array.from(configured)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
        );
      } catch (e) {
        console.error('Failed to load position colors:', e);
      }
    }
    loadPositionColors();
  }, []);

  // Load configured squads for filters
  useEffect(() => {
    (async () => {
      try {
        const squadsData = await apiFetch('/squads');
        const names = Array.from(
          new Set(
            (Array.isArray(squadsData) ? squadsData : [])
              .map((s) => (s?.name ? s.name.trim() : ""))
              .filter(Boolean)
          )
        ).sort((a, b) => a.localeCompare(b));
        setConfiguredSquads(names);
      } catch (e) {
        console.error('Failed to load squads:', e);
      }
    })();
  }, []);
  const hullOptionsForType = useCallback((boatType, currentValue) => {
    const size = HULL_SIZE_BY_TYPE[boatType] || null;
    const base = boatConfig.hulls.filter((h) => !h.size || !size || h.size === size);
    if (currentValue && !base.some((h) => h.value === currentValue)) {
      return [...base, { id: `custom-${currentValue}`, value: currentValue, size: null }];
    }
    return base;
  }, [boatConfig.hulls]);

  function buildAssetChipStyle(statusStyles) {
    return {
      ...ASSET_CHIP_BASE_STYLE,
      backgroundColor: statusStyles.background,
      color: statusStyles.text,
      border: `1px solid ${statusStyles.border}`,
    };
  }

  // quick lookup for roster by id
  const byId = useMemo(() => {
    const m = new Map();
    (roster || []).forEach((r) => m.set(r.user_id ?? r.id, r));
    return m;
  }, [roster]);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch(`/lineup-templates`);
        setTemplates(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("load templates error:", e);
      }
    })();
  }, []);

  async function saveAsTemplate() {
    if (!canEditLineups) return;
    const name = window.prompt("Template name?");
    if (!name) return;
    const includeAssignments = window.confirm("Include athlete seat assignments?");
    try {
      await apiFetch(`/lineup-templates`, {
        method: "POST",
        body: JSON.stringify({ date, name, includeAssignments }),
      });
      const list = await apiFetch(`/lineup-templates`);
      setTemplates(Array.isArray(list) ? list : []);
      alert("Saved!");
    } catch (e) {
      alert(e.message || "Failed to save template");
    }
  }

  async function applyTemplate(templateIdOverride) {
    if (!canEditLineups) return;
    const templateToApply = templateIdOverride || selectedTemplateId;
    if (!templateToApply) return;
    if (!effectivePracticeId) {
      alert("Select or create a practice before applying a template.");
      return;
    }
    const clearExisting = window.confirm("Replace current boats on this day?");
    try {
      console.log('Applying template:', {
        templateId: templateToApply,
        date,
        clearExisting,
        practiceId: effectivePracticeId,
        defaultTeam: teamFilter && teamFilter !== 'all' ? teamFilter : null
      });
      await apiFetch(`/lineup-templates/${templateToApply}/apply`, {
        method: "POST",
        body: JSON.stringify({
          date,
          clearExisting,
          practiceId: effectivePracticeId,
          defaultTeam: teamFilter && teamFilter !== 'all' ? teamFilter : null,
        }),
      });
      console.log('Template applied successfully, reloading boats...');
      await loadBoats();
      console.log('Boats reloaded');
      alert("Applied!");
    } catch (e) {
      console.error('Template application error:', e);
      alert(e.message || "Failed to apply template");
    }
  }

  // ---- Boats now scoped by effectivePracticeId ----
  // boatsLoading tracks only the practice-driven (re)load below, not the many
  // in-place refetches after a user edit (add/remove boat, seat change, etc.) —
  // those already have live data on screen and shouldn't flicker a loading state.
  const [boatsLoading, setBoatsLoading] = useState(true);
  async function loadBoats() {
    try {
      if (!effectivePracticeId) { setBoats([]); return; }
      let url = `/boats/by-practice/${effectivePracticeId}`;
      // Add team filter if it's not "all"
      if (teamFilter && teamFilter !== "all") {
        url += `?team=${encodeURIComponent(teamFilter)}`;
      }
      const data = await apiFetch(url);
      setBoats(Array.isArray(data?.boats) ? data.boats : []);
    } catch (e) {
      console.error("loadBoats error:", e);
      setBoats([]);
    }
  }

  async function loadLineupCheckins() {
    try {
      if (!effectivePracticeId || !teamFilter || teamFilter === "all") {
        setLineupCheckins({});
        return;
      }
      const data = await apiFetch(
        `/lineups/${effectivePracticeId}/checkins?team=${encodeURIComponent(teamFilter)}`
      );
      setLineupCheckins(data?.checkins || {});
    } catch (e) {
      console.error("loadLineupCheckins error:", e);
      setLineupCheckins({});
    }
  }

  useEffect(() => {
    let cancelled = false;
    setBoatsLoading(true);
    loadBoats().finally(() => {
      if (!cancelled) setBoatsLoading(false);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePracticeId, teamFilter]);

  useEffect(() => {
    let intervalId;
    if (isPublished) {
      loadLineupCheckins();
      intervalId = setInterval(() => {
        loadLineupCheckins();
      }, 15000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePracticeId, teamFilter, isPublished]);

  async function addBoat(overrideType = null) {
    if (!canEditLineups) return;
    try {
      if (!effectivePracticeId) return alert("Pick a practice first.");
      if (!teamFilter || teamFilter === "all") {
        return alert("Please select a team before creating a boat.");
      }
      const selectedType = overrideType || type;
      const normalizedType =
        selectedType === "four" || selectedType === "pair" ? selectedType : "eight";
      await apiFetch(`/boats/${effectivePracticeId}`, {
        method: "POST",
        body: JSON.stringify({
          type: normalizedType,
          name: name.trim() || undefined,
          hull: hull.trim() || undefined,
          coach: boatCoach.trim() || undefined,
          oars: oars.trim() || undefined,
          team: teamFilter,
        }),
      });
      setType(normalizedType);
      setName("");
      setHull("");
      setBoatCoach("");
      setOars("");
      await loadBoats();
    } catch (e) {
      console.error("addBoat error:", e);
      alert(e.message || "Failed to add boat");
    }
  }

  async function removeBoat(id) {
    if (!canEditLineups) return;
    if (!effectivePracticeId) return;
    const ok = window.confirm("Remove this boat?");
    if (!ok) return;
    try {
      await apiFetch(`/boats/${effectivePracticeId}/${id}`, { method: "DELETE" });
      await loadBoats();
    } catch (e) {
      console.error("removeBoat error:", e);
      alert(e.message || "Failed to remove boat");
    }
  }

  async function clearBoatSeats(boat) {
    if (!canEditLineups) return;
    if (!effectivePracticeId) return;
    if (!boat?.seats?.length) return;
    const seatsToClear = boat.seats.filter((s) => s?.userId);
    if (!seatsToClear.length) return;
    const ok = window.confirm("Clear all athletes from this boat?");
    if (!ok) return;
    try {
      await Promise.all(
        seatsToClear.map((s) =>
          apiFetch(`/boats/${effectivePracticeId}/${boat.id}/seat`, {
            method: "POST",
            body: JSON.stringify({ seat: s.seat, userId: null }),
          })
        )
      );
      await loadBoats();
    } catch (e) {
      console.error("clearBoatSeats error:", e);
      alert(e.message || "Failed to clear boat seats");
    }
  }

  async function clearAllLineups() {
    if (!canEditLineups) return;
    if (!effectivePracticeId) return;
    if (!boats.length) return;
    const ok = window.confirm(`Delete all boats and lineups? This cannot be undone.`);
    if (!ok) return;
    try {
      await Promise.all(
        boats.map((b) =>
          apiFetch(`/boats/${effectivePracticeId}/${b.id}`, { method: "DELETE" })
        )
      );
      await loadBoats();
    } catch (e) {
      console.error("clearAllLineups error:", e);
      alert(e.message || "Failed to clear lineups");
    }
  }

  async function setSeat(boatId, seat, userId, guestName = null) {
    if (!canEditLineups) return;
    try {
      if (!effectivePracticeId) return;
      await apiFetch(`/boats/${effectivePracticeId}/${boatId}/seat`, {
        method: "POST",
        body: JSON.stringify({ seat, userId, guestName }),
      });
      await loadBoats();
    } catch (e) {
      console.error("setSeat error:", e);
      alert(e.message || "Failed to set seat");
    }
  }

  // Add or remove a coach from a boat's coach field (comma-separated list)
  async function toggleCoachOnBoat(boatId, coachName) {
    if (!canEditLineups) return;
    try {
      const boat = boats.find(b => b.id === boatId);
      if (!boat) return;

      const currentCoaches = extractCoachNames(boat.coach);
      let newCoaches;

      if (currentCoaches.includes(coachName)) {
        // Remove coach
        newCoaches = currentCoaches.filter(c => c !== coachName);
      } else {
        // Add coach
        newCoaches = [...currentCoaches, coachName];
      }

      const newValue = newCoaches.join(', ');
      updateBoatField(boatId, "coach", newValue);
      await flushBoatFieldSave(boatId, "coach", newValue);
    } catch (e) {
      console.error("toggleCoachOnBoat error:", e);
      alert(e.message || "Failed to update coach");
    }
  }

  async function clearCoachesForBoat(boatId) {
    if (!canEditLineups) return;
    try {
      updateBoatField(boatId, "coach", "");
      await flushBoatFieldSave(boatId, "coach", "");
    } catch (e) {
      console.error("clearCoachesForBoat error:", e);
      alert(e.message || "Failed to clear coaches");
    }
  }


  const loadPublishStatus = useCallback(async () => {
    if (!teamFilter || teamFilter === "all") { setIsPublished(false); setPublishedNote(""); return; }
    setPublishLoading(true);
    try {
      const url = effectivePracticeId
        ? `/lineups/${date}/status?team=${encodeURIComponent(teamFilter)}&practiceId=${effectivePracticeId}`
        : `/lineups/${date}/status?team=${encodeURIComponent(teamFilter)}`;
      const r = await apiFetch(url);
      setIsPublished(!!r.published);
      setPublishedNote(r.note || "");
    } catch (e) {
      console.error("loadPublishStatus error:", e);
      setIsPublished(false);
      setPublishedNote("");
    } finally {
      setPublishLoading(false);
    }
  }, [teamFilter, date, effectivePracticeId]);

  useEffect(() => { loadPublishStatus(); }, [loadPublishStatus]);

  function openPublishModal() {
    if (!canEditLineups) return;
    if (!teamFilter || teamFilter === "all") { alert("Pick a team first."); return; }
    if (!effectivePracticeId) { alert("Pick a practice first."); return; }
    setShowPublishModal(true);
  }

  async function confirmPublish(note) {
    await apiFetch(
      `/lineups/${date}/publish?team=${encodeURIComponent(teamFilter)}&practiceId=${effectivePracticeId}`,
      { method: "POST", body: JSON.stringify({ note }) }
    );
    setShowPublishModal(false);
    await loadPublishStatus();
  }

  async function unpublishNow() {
    if (!canEditLineups) return;
    if (!teamFilter || teamFilter === "all") { alert("Pick a team first."); return; }
    const url = effectivePracticeId
      ? `/lineups/${date}/publish?team=${encodeURIComponent(teamFilter)}&practiceId=${effectivePracticeId}`
      : `/lineups/${date}/publish?team=${encodeURIComponent(teamFilter)}`;
    await apiFetch(url, { method: "DELETE" });
    await loadPublishStatus();
  }

  // ---- Debounced autosave for boat meta (practice-scoped) ----
  const saveTimers = useRef(new Map()); // key: `${boatId}:${field}` -> timeoutId

  async function apiUpdateBoatField(boatId, patch) {
    if (!effectivePracticeId) return null;
    return await apiFetch(`/boats/${effectivePracticeId}/${boatId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  }

  // Apply a server response that may contain a resolved launch back to local state
  function applyBoatPatchResponse(boatId, field, result) {
    if (field === "coach" && result && Object.prototype.hasOwnProperty.call(result, "launch")) {
      setBoats((prev) =>
        prev.map((boat) => (boat.id === boatId ? { ...boat, launch: result.launch ?? null } : boat))
      );
    }
  }

  function updateBoatField(boatId, field, value) {
    setBoats((prev) =>
      prev.map((boat) => (boat.id === boatId ? { ...boat, [field]: value } : boat))
    );

    const key = `${boatId}:${field}`;
    if (saveTimers.current.has(key)) clearTimeout(saveTimers.current.get(key));

    const t = setTimeout(async () => {
      saveTimers.current.delete(key);
      try {
        const result = await apiUpdateBoatField(boatId, { [field]: value });
        applyBoatPatchResponse(boatId, field, result);
      } catch (err) {
        console.error("autosave failed:", err);
        alert("Autosave failed. Reverting.");
        await loadBoats();
      }
    }, 500);
    saveTimers.current.set(key, t);
  }

  async function flushBoatFieldSave(boatId, field, value) {
    const key = `${boatId}:${field}`;
    if (saveTimers.current.has(key)) {
      clearTimeout(saveTimers.current.get(key));
      saveTimers.current.delete(key);
    }
    try {
      const result = await apiUpdateBoatField(boatId, { [field]: value });
      applyBoatPatchResponse(boatId, field, result);
    } catch (err) {
      console.error("save on blur failed:", err);
      alert("Save failed. Reverting.");
      await loadBoats();
    }
  }

  // Load maintenance tasks for a specific asset
  async function loadAssetTasks(assetName, assetType) {
    try {
      const data = await apiFetch('/equipment-tasks');
      const filtered = data.filter(task =>
        task.equipment_name === assetName &&
        task.equipment_type === assetType &&
        task.status !== 'completed'
      );
      setAssetTasks(filtered);
    } catch (e) {
      console.error('Failed to load asset tasks:', e);
      setAssetTasks([]);
    }
  }

  async function openAssetModal(assetName, assetType, displayLabel) {
    setSelectedAsset({ name: assetName, type: assetType, label: displayLabel });
    await loadAssetTasks(assetName, assetType);
    setAssetModalOpen(true);
  }

  async function closeAssetModal() {
    setAssetModalOpen(false);
    setSelectedAsset(null);
    setAssetTasks([]);
    // Refresh the list of assets with open tasks
    try {
      const data = await apiFetch('/equipment-tasks');
      const openTasks = data.filter(task => task.status !== 'completed');
      const assetKeys = new Set(
        openTasks.map(task => `${task.equipment_type}:${task.equipment_name}`)
      );
      setAssetsWithTasks(assetKeys);
    } catch (e) {
      console.error('Failed to refresh equipment tasks:', e);
    }
  }

  // Check if an asset has open maintenance tasks
  function assetHasOpenTasks(assetName, assetType) {
    return assetsWithTasks.has(`${assetType}:${assetName}`);
  }

  // ----- DnD helpers -----
  function onDragStartAthlete(e, user, currentBoatId = null, currentSeat = null) {
    if (!canEditLineups) return;
    const id = user.user_id ?? user.id;
    e.dataTransfer.setData("text/plain", JSON.stringify({
      userId: id,
      type: 'athlete',
      currentBoatId,
      currentSeat
    }));
    e.dataTransfer.effectAllowed = "move";
  }
  function onDragStartCoach(e, coachData) {
    if (!canEditLineups) return;
    e.dataTransfer.setData("text/plain", JSON.stringify({
      coachId: coachData.id,
      coachName: coachData.coachName,
      type: 'coach'
    }));
    e.dataTransfer.effectAllowed = "move";
  }
  function onDragOver(e) {
    if (!canEditLineups) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
  async function onDropToSeat(e, boatId, seat) {
    if (!canEditLineups) return;
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (!data?.userId) return;

      if (!effectivePracticeId) return;

      // Perform atomic move: clear old seat (if any) and set new seat in one go
      if (data.currentBoatId && data.currentSeat) {
        // Moving from one seat to another - do both operations then refresh once
        await Promise.all([
          apiFetch(`/boats/${effectivePracticeId}/${data.currentBoatId}/seat`, {
            method: "POST",
            body: JSON.stringify({ seat: data.currentSeat, userId: null }),
          }),
          apiFetch(`/boats/${effectivePracticeId}/${boatId}/seat`, {
            method: "POST",
            body: JSON.stringify({ seat, userId: data.userId }),
          })
        ]);
        // Refresh UI once after both operations complete
        await loadBoats();
      } else {
        // Assigning from unassigned panel - just set the seat
        await setSeat(boatId, seat, data.userId);
      }
    } catch (err) {
      console.error("Drop to seat error:", err);
      alert(err.message || "Failed to move athlete");
    }
  }
  async function onDropCoachToBoat(e, boatId) {
    if (!canEditLineups) return;
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (data?.type === 'coach' && data?.coachName) {
        // Add coach to the boat (supports multiple coaches)
        await toggleCoachOnBoat(boatId, data.coachName);
      }
    } catch (err) {
      console.error("Drop coach error:", err);
    }
  }
  function onDropToUnassigned(e) {
    if (!canEditLineups) return;
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      const userId = data?.userId;
      if (!userId) return;
      const b = boats.find((b) => (b.seats || []).some((s) => s.userId === userId));
      if (!b) return;
      const seat = b.seats.find((s) => s.userId === userId)?.seat;
      if (!seat) return;
      setSeat(b.id, seat, null);
    } catch {}
  }

  // ----- Unassigned roster panel data -----
  const [attendanceByUser, setAttendanceByUser] = useState({});
  useEffect(() => {
    const normalizeStatus = (val) => {
      const s = typeof val === "string" ? val.toLowerCase() : "";
      if (s === "available" || s === "present") return "present";
      if (s === "not available" || s === "unavailable" || s === "absent") return "absent";
      return null;
    };
    const loadAttendance = async () => {
      if (!effectivePracticeId) {
        setAttendanceByUser({});
        return;
      }
      try {
        const rows = await apiFetch(`/attendance/by-practice/${effectivePracticeId}`);
        const map = {};
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          const id = row.userId ?? row.user_id ?? row.id;
          const st = normalizeStatus(row.status);
          if (id && st) map[id] = st;
        });
        setAttendanceByUser(map);
      } catch (e) {
        console.error("loadAttendance error:", e);
        setAttendanceByUser({});
      }
    };
    loadAttendance();
  }, [effectivePracticeId]);

  const displayedBoats = useMemo(() => boats || [], [boats]);

  const assignedUserIds = useMemo(() => {
    const s = new Set();
    displayedBoats.forEach((b) => (b.seats || []).forEach((seat) => seat.userId && s.add(seat.userId)));
    return s;
  }, [displayedBoats]);

  // Track which coaches are assigned to boats
  const assignedCoachNames = useMemo(() => {
    const names = new Set();
    displayedBoats.forEach((b) => {
      extractCoachNames(b.coach).forEach((name) => names.add(name));
    });
    return names;
  }, [displayedBoats]);

  const rosterSquadOptions = useMemo(() => {
    const set = new Set();
    (roster || []).forEach((r) => {
      const value = r.squad ? String(r.squad).trim() : "";
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [roster]);

  const configSquadOptions = useMemo(() => {
    const set = new Set();
    (configuredSquads || []).forEach((name) => {
      const value = name ? String(name).trim() : "";
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [configuredSquads]);

  const squadFilterOptions = configSquadOptions.length ? configSquadOptions : rosterSquadOptions;

  const filteredPractices = useMemo(() => {
    return dayPractices.filter(p => {
      const practiceTeam = String(p.team || "").trim().toUpperCase();
      const isAllTeams = !practiceTeam || practiceTeam === "ALL_TEAMS" || practiceTeam === "ALL TEAMS";
      const selectedTeam = String(teamFilter || "").trim().toUpperCase();
      if (selectedTeam && selectedTeam !== "ALL" && !isAllTeams && practiceTeam !== selectedTeam) return false;
      return true;
    });
  }, [dayPractices, teamFilter]);

  const rosterPositionOptions = useMemo(() => {
    const set = new Set();
    (roster || []).forEach((r) => {
      const value = canonicalPosition(r.position);
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [roster]);

  const configPositionOptions = useMemo(() => {
    const set = new Set();
    (configuredPositions || []).forEach((name) => {
      const value = canonicalPosition(name);
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [configuredPositions]);

  const positionFilterOptions = configPositionOptions.length ? configPositionOptions : rosterPositionOptions;

  // When filtering by team, compare case-insensitively
  const normFilter = String(teamFilter || "").trim().toLowerCase();
  const filteredRoster = useMemo(() => {
    let result = roster || [];
    if (normFilter && normFilter !== "all") {
      result = result.filter((r) => String(r.team || "").trim().toLowerCase() === normFilter);
    }
    return result;
  }, [roster, normFilter]);
  const unassigned = useMemo(() => {
    const set = assignedUserIds;
    return (filteredRoster || []).filter((r) => !set.has(r.id));
  }, [filteredRoster, assignedUserIds]);

  const pickerTeamOptions = useMemo(() => {
    const teamsSet = new Set();
    unassigned.forEach((u) => {
      const t = u.team && String(u.team).trim();
      if (t) teamsSet.add(t);
    });
    return Array.from(teamsSet).sort((a, b) => a.localeCompare(b));
  }, [unassigned]);

  const pickerSquadOptions = squadFilterOptions;
  const pickerPositionOptions = positionFilterOptions;

  const pickerAgeGroupOptions = useMemo(() => {
    const ageGroups = new Set();
    unassigned.forEach((u) => {
      if (u.age_group) {
        ageGroups.add(String(u.age_group).trim());
      }
    });
    return Array.from(ageGroups).sort((a, b) => a.localeCompare(b));
  }, [unassigned]);

  const modalCandidates = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return unassigned.filter((u) => {
      if (q && !u.name.toLowerCase().includes(q)) return false;
      const athleteTeam = u.team ? String(u.team).trim() : "";
      if (pickerTeam !== "all" && athleteTeam !== pickerTeam) return false;
      const athleteSquad = u.squad ? String(u.squad).trim() : "";
      if (pickerSquad !== "all" && athleteSquad !== pickerSquad) return false;
      const pos = canonicalPosition(u.position);
      if (pickerPosition !== "all" && pos !== pickerPosition) return false;
      const athleteAgeGroup = u.age_group ? String(u.age_group).trim() : "";
      if (pickerAgeGroup !== "all" && athleteAgeGroup !== pickerAgeGroup) return false;
      if (pickerAvailability !== "all") {
        const st = attendanceByUser[u.id];
        if (pickerAvailability === "available" && st !== "present") return false;
        if (pickerAvailability === "unavailable" && st !== "absent") return false;
        if (pickerAvailability === "noresponse" && st != null) return false;
      }
      return true;
    });
  }, [unassigned, pickerSearch, pickerTeam, pickerSquad, pickerPosition, pickerAgeGroup, pickerAvailability, attendanceByUser]);

  const coachPickerBoat = useMemo(() => {
    return boats.find((b) => b.id === coachPickerBoatId) ?? null;
  }, [boats, coachPickerBoatId]);

  const coachPickerAssignedNames = useMemo(() => {
    return extractCoachNames(coachPickerBoat?.coach);
  }, [coachPickerBoat]);

  const coachPickerOptions = useMemo(() => {
    const q = coachPickerSearch.trim().toLowerCase();
    const statusFilter = coachPickerStatus;
    const source = Array.isArray(coaches) ? coaches : [];
    const filtered = source.filter((c) => {
      const name = c.coachName ? c.coachName.toLowerCase() : "";
      if (q && !name.includes(q)) return false;
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      return true;
    });
    const rank = { present: 0, pending: 1, absent: 2 };
    return filtered.sort((a, b) => {
      const rA = rank[a.status] ?? 3;
      const rB = rank[b.status] ?? 3;
      if (rA !== rB) return rA - rB;
      return (a.coachName || "").localeCompare(b.coachName || "");
    });
  }, [coaches, coachPickerSearch, coachPickerStatus]);

  const assetUsageCounts = useMemo(() => {
    const counts = {
      hull: new Map(),
      oars: new Map(),
      cox_boxes: new Map(),
      speed_coach: new Map(),
      launch: new Map(),
    };
    const bump = (map, value) => {
      if (!value) return;
      const key = String(value).trim();
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    };
    (boats || []).forEach((b) => {
      bump(counts.hull, b.hull);
      bump(counts.oars, b.oars);
      bump(counts.cox_boxes, b.coxBox);
      bump(counts.speed_coach, b.speedCoach);
      bump(counts.launch, b.launch);
    });
    return counts;
  }, [boats]);

  const isAssetDuplicate = useCallback((assetType, value) => {
    if (!value) return false;
    const map = assetUsageCounts[assetType];
    if (!map) return false;
    const key = String(value).trim();
    return key && (map.get(key) || 0) > 1;
  }, [assetUsageCounts]);
  const getAssetUsageCount = useCallback((assetType, value) => {
    if (!value) return 0;
    const map = assetUsageCounts[assetType];
    if (!map) return 0;
    const key = String(value).trim();
    return key ? (map.get(key) || 0) : 0;
  }, [assetUsageCounts]);

  useEffect(() => {
    if (!pickerOpen) {
      setPickerSearch("");
      return;
    }
    if (seatSearchRef.current) {
      seatSearchRef.current.focus();
      seatSearchRef.current.select();
    }
  }, [pickerOpen]);

  useEffect(() => {
    if (seatSearchInputRef.current) {
      seatSearchInputRef.current.focus();
      seatSearchInputRef.current.select();
    }
  }, [seatSearchCtx]);
  useEffect(() => {
    if (!coachPickerOpen) {
      setCoachPickerSearch("");
      setCoachPickerStatus("all");
    }
  }, [coachPickerOpen]);
  useEffect(() => {
    if (!actionsMenuOpen) return;
    function handleClickAway(e) {
      if (!actionsMenuRef.current) return;
      if (!actionsMenuRef.current.contains(e.target)) {
        setActionsMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [actionsMenuOpen]);
function openSeatPicker(boatId, seat, currentUserId = null) {
  if (!canEditLineups) return;
  setPickerCtx({ boatId, seat, currentUserId });
  setPickerOpen(true);
}

function closeSeatPicker() {
  setPickerOpen(false);
  setPickerCtx(null);
}
async function assignFromPicker(userId) {
  if (!canEditLineups) return;
  if (!pickerCtx) return;
  await setSeat(pickerCtx.boatId, pickerCtx.seat, userId);
  closeSeatPicker();
}
async function assignFillerFromPicker() {
  if (!canEditLineups) return;
  if (!pickerCtx) return;
  const name = pickerFillerName.trim();
  if (!name) return;
  await setSeat(pickerCtx.boatId, pickerCtx.seat, null, name);
  setPickerFillerName("");
  setPickerShowFiller(false);
  closeSeatPicker();
}
async function clearSeatFromPicker() {
  if (!canEditLineups) return;
  if (!pickerCtx) return;
  await setSeat(pickerCtx.boatId, pickerCtx.seat, null);
  closeSeatPicker();
}
function openCoachPicker(boatId) {
  if (!canEditLineups) return;
  setCoachPickerBoatId(boatId);
  setCoachPickerOpen(true);
}
function closeCoachPicker() {
  setCoachPickerOpen(false);
  setCoachPickerBoatId(null);
}
async function toggleCoachFromPicker(coachName) {
  if (!canEditLineups) return;
  if (!coachPickerBoatId) return;
  const name = coachName?.trim();
  if (!name) return;
  await toggleCoachOnBoat(coachPickerBoatId, name);
}
async function clearCoachAssignmentsFromPicker() {
  if (!canEditLineups) return;
  if (!coachPickerBoatId) return;
  await clearCoachesForBoat(coachPickerBoatId);
}

  function openAddBoatModal() {
    if (!canEditLineups) return;
    setBoatActionType(type);
    setBoatActionOpen(true);
    setActionsMenuOpen(false);
  }

  function closeAddBoatModal() {
    setBoatActionOpen(false);
  }

  async function submitAddBoatModal() {
    await addBoat(boatActionType);
    closeAddBoatModal();
  }

  function openApplyTemplateModal() {
    if (!canEditLineups) return;
    if (!templates.length) {
      alert("No templates available.");
      return;
    }
    setTemplateActionId(selectedTemplateId || templates[0].id);
    setTemplateActionOpen(true);
    setActionsMenuOpen(false);
  }

  function closeApplyTemplateModal() {
    setTemplateActionOpen(false);
  }

  async function submitApplyTemplateModal() {
    const templateId = templateActionId || templates[0]?.id;
    if (!templateId) {
      alert("Pick a template first.");
      return;
    }
    setSelectedTemplateId(templateId);
    await applyTemplate(templateId);
    closeApplyTemplateModal();
  }
  function onDragStartUser(_id) {}

  // ---- Helpers
  function fmtPracticeOption(p) {
    const timeLabel = formatPracticeTimeRange(p) || "All day";
    const typeRaw = String(p.type || "").trim().toLowerCase();
    const typeLabel = typeRaw.includes("regatta")
      ? "Regatta"
      : typeRaw.includes("event")
        ? "Event"
        : "Practice";
    return `${timeLabel} — ${typeLabel}${p.squad ? ` — ${String(p.squad).toUpperCase()}` : ""}`;
  }

  function isPracticeType(p) {
    const raw = String(p?.type || "").trim().toLowerCase();
    if (!raw) return true; // treat missing type as Practice
    if (raw.includes("practice")) return true;
    if (raw.includes("regatta")) return false;
    if (raw.includes("event")) return false;
    return true;
  }

  // ---- UI
  return (
    <div className="stack">
      {/* Sticky pill toolbar */}
      <div className="lineup-pill-bar">
        <div className="lineup-pill-filters">
          <select
            id="team-filter"
            className="lineup-pill-select"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            title="Team"
          >
            <option value="all">All teams</option>
            {teams.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          <select
            id="practice-picker"
            className="lineup-pill-select lineup-pill-select--wide"
            value={filteredPractices.some(p => p.id === effectivePracticeId) ? effectivePracticeId || "" : (filteredPractices[0]?.id || "")}
            onChange={(e) => setSelectedPracticeId(e.target.value ? Number(e.target.value) : null)}
            title="Practice"
          >
            {filteredPractices.length === 0 && <option value="">No matching practices</option>}
            {filteredPractices.map((p) => (
              <option key={p.id} value={p.id}>{fmtPracticeOption(p)}</option>
            ))}
          </select>

          {!canEditLineups && isPublished && (
            <span className="lineup-pill-badge lineup-pill-badge--published">Published</span>
          )}
        </div>

        <div className="lineup-pill-actions" ref={actionsMenuRef}>
          {/* squad pill removed from here — moved to filters */}

          {canEditLineups && !publishLoading && teamFilter && teamFilter !== "all" && (
            <button
              className={`lineup-pill-publish-btn${isPublished ? " published" : ""}`}
              onClick={isPublished ? unpublishNow : openPublishModal}
              title={isPublished ? "Unpublish lineups (hide from athletes)" : "Publish lineups (visible to all athletes)"}
            >
              {isPublished ? "Unpublish" : "Publish"}
            </button>
          )}

          {canEditLineups && (
            <button
              type="button"
              className="lineup-pill-more-btn"
              onClick={() => setActionsMenuOpen((open) => !open)}
              aria-label="Actions menu"
            >
              <FaEllipsisH />
            </button>
          )}

          {actionsMenuOpen && (
            <div className="lineup-actions-menu">
              <button type="button" onClick={openAddBoatModal} disabled={!effectivePracticeId}>
                Add Boat
              </button>
              <button type="button" onClick={openApplyTemplateModal} disabled={!templates.length}>
                Import Template
              </button>
              <button type="button" onClick={() => { setActionsMenuOpen(false); saveAsTemplate(); }}>
                Save Template
              </button>
              <button
                type="button"
                style={{ color: "var(--danger, #e55)" }}
                onClick={() => { setActionsMenuOpen(false); clearAllLineups(); }}
                disabled={!displayedBoats.length}
              >
                Clear Lineups
              </button>
            </div>
          )}
        </div>
      </div>


      {(isTouchDevice || isCompactLineup) && (
        <div className="stack" style={{ gap: 10 }}>
          <div className="card" style={{ padding: 12 }}>
            <button
              className="btn ghost small"
              type="button"
              onClick={() => setShowMobileCoaches((prev) => !prev)}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", color: 'var(--text)' }}
            >
              {showMobileCoaches ? <FaChevronUp /> : <FaChevronDown />}
              <span style={{ fontWeight: 600 }}>Coach Availability</span>
            </button>
            {showMobileCoaches && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                {coaches.length === 0 ? (
                  <div className="help" style={{ padding: 8 }}>No coaches found</div>
                ) : (
                  coaches.map((coachData) => {
                    const isAssigned = assignedCoachNames.has(coachData.coachName);
                    const statusClass =
                      coachData.status === 'present' ? 'available' :
                      coachData.status === 'absent' ? 'absent' : 'pending';
                    const statusIcon =
                      coachData.status === 'present' ? <FaCheckCircle className="statusIcon present lineup-circle" style={{ fontSize: 14 }} /> :
                      coachData.status === 'absent' ? <AbsentStatusIcon size={14} /> :
                      <FaQuestionCircle className="statusIcon pending" style={{ fontSize: 14 }} />;
                    return (
                      <div
                        key={coachData.id}
                        className={`chip block coach-chip ${statusClass}`}
                        style={{
                          padding: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          backgroundColor:
                            coachData.status === 'present' ? 'rgba(52,211,153,0.15)' :
                            coachData.status === 'absent' ? 'rgba(248,113,113,0.15)' : 'var(--bg-elevated)',
                          color: 'var(--text-strong)',
                          border: '2px solid',
                          borderColor:
                            isAssigned ? '#3b82f6' :
                            coachData.status === 'present' ? 'rgba(52,211,153,0.4)' :
                            coachData.status === 'absent' ? 'rgba(248,113,113,0.4)' : 'var(--border)',
                          position: 'relative'
                        }}
                        title={coachData.coachName}
                      >
                        {statusIcon}
                        <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{coachData.coachName}</span>
                        {isAssigned && (
                          <span
                            style={{
                              marginLeft: 'auto',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              borderRadius: '50%',
                              width: '18px',
                              height: '18px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                            title="Assigned to boat"
                          >
                            ✓
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 12 }}>
            <button
              className="btn ghost small"
              type="button"
              onClick={() => setShowMobileRoster((prev) => !prev)}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", color: 'var(--text)' }}
            >
              {showMobileRoster ? <FaChevronUp /> : <FaChevronDown />}
              <span style={{ fontWeight: 600 }}>Roster Availability</span>
            </button>
            {showMobileRoster && (
              <div style={{ marginTop: 10 }}>
                <UnassignedRosterPanel
                  roster={filteredRoster}
                  attendanceByUser={attendanceByUser}
                  assignedUserIds={assignedUserIds}
                  onDragStartUser={undefined}
                  canEdit={false}
                  squadOptions={squadFilterOptions}
                  positionOptions={positionFilterOptions}
                  positionColors={positionColors}
                  coachTeam={coachTeam}
                  teamFilter={teamFilter !== "all" ? teamFilter : null}
                  squadFilter={null}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Columns scroller: Coaches + Unassigned + each boat */}
      <div ref={lineupWrapperRef} style={(isTouchDevice || isCompactLineup) && lineupZoom < 1 ? { overflow: 'hidden' } : undefined}>
      <div className="lineup-layout" style={(isTouchDevice || isCompactLineup) && lineupZoom < 1 ? {
        transform: `scale(${lineupZoom})`,
        transformOrigin: 'top left',
        width: `${(1 / lineupZoom) * 100}%`,
      } : undefined}>
        {!isTouchDevice && !isCompactLineup && (coachPanelCollapsed || rosterPanelCollapsed) && (
          <div className="lineup-left-rail">
            {coachPanelCollapsed && (
              <button
                type="button"
                className="lineup-panel-tab"
                onClick={() => setCoachPanelCollapsed(false)}
                aria-label="Show coaches"
                title="Show coaches"
              >
                <span>Coaches</span>
              </button>
            )}
            {rosterPanelCollapsed && (
              <button
                type="button"
                className="lineup-panel-tab"
                onClick={() => setRosterPanelCollapsed(false)}
                aria-label="Show roster availability"
                title="Show roster availability"
              >
                <span>Roster</span>
              </button>
            )}
          </div>
        )}
        <div ref={scrollerRef} className={`lineupScroller coach-lineup${(isTouchDevice || isCompactLineup) && lineupZoom < 1 ? ' lineup-zoomed-out' : ''}`}>
        {/* Coaches column (hidden on touch devices) */}
        {!isTouchDevice && !isCompactLineup && (
          coachPanelCollapsed ? null : (
            <div className="lineupCol" title="Drag coaches to boats">
              <div className="colHeader">
                <strong>Coaches</strong>
                <button
                  type="button"
                  className="lineup-panel-toggle"
                  onClick={() => setCoachPanelCollapsed(true)}
                  aria-label="Collapse coaches panel"
                  title="Collapse"
                >
                  <FaChevronLeft />
                </button>
              </div>
              <div className="colBody">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {coaches.length === 0 ? (
                    <div className="help" style={{ padding: 8 }}>No coaches found</div>
                  ) : (
                    coaches.map((coachData) => {
                      const isAssigned = assignedCoachNames.has(coachData.coachName);
                      const statusClass =
                        coachData.status === 'present' ? 'available' :
                        coachData.status === 'absent' ? 'absent' : 'pending';
                      const statusIcon =
                        coachData.status === 'present' ? <FaCheckCircle className="statusIcon present lineup-circle" style={{ fontSize: 14 }} /> :
                        coachData.status === 'absent' ? <AbsentStatusIcon size={14} /> :
                        <FaQuestionCircle className="statusIcon pending" style={{ fontSize: 14 }} />;
                      return (
                      <div
                        key={coachData.id}
                        className={`chip block coach-chip ${statusClass}`}
                        draggable={canEditLineups}
                        onDragStart={canEditLineups ? (e) => onDragStartCoach(e, coachData) : undefined}
                        style={{
                          cursor: canEditLineups ? 'grab' : 'default',
                          padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            backgroundColor:
                              coachData.status === 'present' ? 'rgba(52,211,153,0.15)' :
                              coachData.status === 'absent' ? 'rgba(248,113,113,0.15)' : 'var(--bg-elevated)',
                            color: 'var(--text-strong)',
                            border: '2px solid',
                            borderColor:
                              isAssigned ? '#3b82f6' :
                              coachData.status === 'present' ? 'rgba(52,211,153,0.4)' :
                              coachData.status === 'absent' ? 'rgba(248,113,113,0.4)' : 'var(--border)',
                            position: 'relative'
                          }}
                          title={coachData.coachName}
                        >
                          {statusIcon}
                          <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{coachData.coachName}</span>
                          {isAssigned && (
                            <span
                              style={{
                                marginLeft: 'auto',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}
                              title="Assigned to boat"
                            >
                              ✓
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )
        )}

        {/* Unassigned column (hidden on touch devices) */}
        {!isTouchDevice && !isCompactLineup && (
          rosterPanelCollapsed ? null : (
            <div
              className="lineupCol"
              onDragOver={canEditLineups ? onDragOver : undefined}
              onDrop={canEditLineups ? onDropToUnassigned : undefined}
              title={canEditLineups ? "Drag here to unassign" : "Athletes (view only)"}
            >
              <div className="colHeader">
                <strong>Roster Availability</strong>
                <button
                  type="button"
                  className="lineup-panel-toggle"
                  onClick={() => setRosterPanelCollapsed(true)}
                  aria-label="Collapse roster availability panel"
                  title="Collapse"
                >
                  <FaChevronLeft />
                </button>
              </div>
              <div className="colBody">
                <UnassignedRosterPanel
                  roster={filteredRoster}
                  attendanceByUser={attendanceByUser}
                  assignedUserIds={assignedUserIds}
                  onDragStartUser={canEditLineups ? onDragStartUser : undefined}
                  canEdit={canEditLineups}
                  squadOptions={squadFilterOptions}
                  positionOptions={positionFilterOptions}
                  positionColors={positionColors}
                  coachTeam={coachTeam}
                  teamFilter={teamFilter !== "all" ? teamFilter : null}
                  squadFilter={null}
                />
              </div>
            </div>
          )
        )}

        {/* Empty state when no boats exist (only once the practice for this date has
            actually resolved and boats have finished loading, so this doesn't flash
            in ahead of the real data) */}
        {practicesReadyForDate && !boatsLoading && boats.length === 0 && (
          <div className="lineupCol" style={{ maxWidth: 320 }}>
            <div className="colHeader">
              <strong>Lineups</strong>
            </div>
            <div
              className="colBody"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "32px 16px",
                gap: 12,
                minHeight: 220,
                color: "#6b7280",
              }}
            >
              <FaClipboardCheck style={{ fontSize: 36, color: "#cbd5f5" }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>No boats yet</div>
                <div style={{ fontSize: 14 }}>
                  Select a template or add a boat above to start building this lineup.
                </div>
                {canEditLineups && templates.length > 0 && (
                  <button
                    type="button"
                    onClick={openApplyTemplateModal}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      marginTop: 8,
                      color: "var(--primary)",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Import a template
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Boat columns */}
        {displayedBoats.map((b) => {
          const hullStatusInfo = b.hull ? getAssetStatusInfo(b.hull, "hull") : null;
          const hullChipStyle = hullStatusInfo ? buildAssetChipStyle(hullStatusInfo.styles) : null;
          const oarStatusInfo = b.oars ? getAssetStatusInfo(b.oars, "oars") : null;
          const oarChipStyle = oarStatusInfo ? buildAssetChipStyle(oarStatusInfo.styles) : null;
          const coxStatusInfo = b.coxBox ? getAssetStatusInfo(b.coxBox, "cox_boxes") : null;
          const coxChipStyle = coxStatusInfo ? buildAssetChipStyle(coxStatusInfo.styles) : null;
          const launchStatusInfo = b.launch ? getAssetStatusInfo(b.launch, "launch") : null;
          const launchChipStyle = launchStatusInfo ? buildAssetChipStyle(launchStatusInfo.styles) : null;
          const speedCoachStatusInfo = b.speedCoach ? getAssetStatusInfo(b.speedCoach, "speed_coach") : null;
          const speedCoachChipStyle = speedCoachStatusInfo ? buildAssetChipStyle(speedCoachStatusInfo.styles) : null;
          const coachNames = extractCoachNames(b.coach);

          return (
            <div key={b.id} className="lineupCol">
            <div className="colHeader" title={`${b.type}`}>
              {/* Asset chips section */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                {/* Squad */}
                {/* Hull */}
                <div className="boat-asset-row boat-asset-row-hull" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', minWidth: 45 }}>Hull:</span>
                  {b.hull ? (
                    <div
                      className="chip"
                      onClick={() => openAssetModal(b.hull, 'hull', `Hull: ${b.hull}`)}
                      style={hullChipStyle}
                      title={`Hull: ${b.hull}${hullStatusInfo ? ` • Status: ${hullStatusInfo.label}` : ''}\nClick to view maintenance tasks`}
                    >
                      {b.hull}
                      {isAssetDuplicate("hull", b.hull) && (
                        <FaExclamationTriangle style={{ color: '#6d28d9', fontSize: '11px' }} title="Asset already used on another boat" />
                      )}
                      {assetHasOpenTasks(b.hull, 'hull') && (
                        <FaExclamationTriangle style={{ color: '#f59e0b', fontSize: '11px' }} title="Has open maintenance tasks" />
                      )}
                    </div>
                  ) : canEditLineups ? (
                    <select
                      className="input boat-asset-select"
                      value=""
                      onChange={(e) => {
                        updateBoatField(b.id, "hull", e.target.value);
                        flushBoatFieldSave(b.id, "hull", e.target.value);
                      }}
                      style={{ flex: "0 1 auto" }}
                    >
                      <option value="">Select...</option>
                      {hullOptionsForType(b.type, b.hull).map(h => (
                        <option key={h.id ?? h.value} value={h.value}>{h.value}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>Not set</span>
                  )}
                  {b.hull && canEditLineups && (
                    <button
                      className="btn ghost"
                      style={{ padding: '2px 6px', fontSize: '10px', minHeight: 'auto' }}
                      onClick={() => {
                        updateBoatField(b.id, "hull", "");
                        flushBoatFieldSave(b.id, "hull", "");
                      }}
                      title="Clear hull"
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Oars */}
                <div className="boat-asset-row" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', minWidth: 45 }}>Oars:</span>
                  {b.oars ? (
                    <div
                      className="chip"
                      onClick={() => openAssetModal(b.oars, 'oars', `Oars: ${b.oars}`)}
                      style={oarChipStyle}
                      title={`Oars: ${b.oars}${oarStatusInfo ? ` • Status: ${oarStatusInfo.label}` : ''}\nClick to view maintenance tasks`}
                    >
                      {b.oars}
                      {isAssetDuplicate("oars", b.oars) && (
                        <FaExclamationTriangle style={{ color: '#6d28d9', fontSize: '11px' }} title="Asset already used on another boat" />
                      )}
                      {assetHasOpenTasks(b.oars, 'oars') && (
                        <FaExclamationTriangle style={{ color: '#f59e0b', fontSize: '11px' }} title="Has open maintenance tasks" />
                      )}
                    </div>
                  ) : canEditLineups ? (
                    <select
                      className="input boat-asset-select"
                      value=""
                      onChange={(e) => {
                        updateBoatField(b.id, "oars", e.target.value);
                        flushBoatFieldSave(b.id, "oars", e.target.value);
                      }}
                      style={{ flex: "0 1 auto" }}
                    >
                      <option value="">Select...</option>
                      {boatConfig.oars.map(o => (
                        <option key={o.id ?? o.value} value={o.value}>{o.value}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>Not set</span>
                  )}
                  {b.oars && canEditLineups && (
                    <button
                      className="btn ghost"
                      style={{ padding: '2px 6px', fontSize: '10px', minHeight: 'auto' }}
                      onClick={() => {
                        updateBoatField(b.id, "oars", "");
                        flushBoatFieldSave(b.id, "oars", "");
                      }}
                      title="Clear oars"
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Cox Box */}
                <div className="boat-asset-row" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', minWidth: 45 }}>Box:</span>
                  {b.coxBox ? (
                    <div
                      className="chip"
                      onClick={() => openAssetModal(b.coxBox, 'cox_boxes', `Cox Box: ${b.coxBox}`)}
                      style={coxChipStyle}
                      title={`Cox Box: ${b.coxBox}${coxStatusInfo ? ` • Status: ${coxStatusInfo.label}` : ''}\nClick to view maintenance tasks`}
                    >
                      {b.coxBox}
                      {isAssetDuplicate("cox_boxes", b.coxBox) && (
                        <FaExclamationTriangle style={{ color: '#6d28d9', fontSize: '11px' }} title="Asset already used on another boat" />
                      )}
                      {assetHasOpenTasks(b.coxBox, 'cox_boxes') && (
                        <FaExclamationTriangle style={{ color: '#f59e0b', fontSize: '11px' }} title="Has open maintenance tasks" />
                      )}
                    </div>
                  ) : canEditLineups ? (
                    <select
                      className="input boat-asset-select"
                      value=""
                      onChange={(e) => {
                        updateBoatField(b.id, "coxBox", e.target.value);
                        flushBoatFieldSave(b.id, "coxBox", e.target.value);
                      }}
                      style={{ flex: "0 1 auto" }}
                    >
                      <option value="">Select...</option>
                      {boatConfig.coxBoxes.map(c => (
                        <option key={c.id ?? c.value} value={c.value}>{c.value}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>Not set</span>
                  )}
                  {b.coxBox && canEditLineups && (
                    <button
                      className="btn ghost"
                      style={{ padding: '2px 6px', fontSize: '10px', minHeight: 'auto' }}
                      onClick={() => {
                        updateBoatField(b.id, "coxBox", "");
                        flushBoatFieldSave(b.id, "coxBox", "");
                      }}
                      title="Clear cox box"
                    >
                      ×
                    </button>
                  )}
                </div>

              

                {/* Speed Coach */}
                <div className="boat-asset-row" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', minWidth: 45 }}>Speed:</span>
                  {b.speedCoach ? (
                    <div
                      className="chip"
                      onClick={() => openAssetModal(b.speedCoach, 'speed_coach', `Speed Coach: ${b.speedCoach}`)}
                      style={speedCoachChipStyle}
                      title={`Speed Coach: ${b.speedCoach}${speedCoachStatusInfo ? ` • Status: ${speedCoachStatusInfo.label}` : ''}\nClick to view maintenance tasks`}
                    >
                      {b.speedCoach}
                      {isAssetDuplicate("speed_coach", b.speedCoach) && (
                        <FaExclamationTriangle style={{ color: '#6d28d9', fontSize: '11px' }} title="Asset already used on another boat" />
                      )}
                      {assetHasOpenTasks(b.speedCoach, 'speed_coach') && (
                        <FaExclamationTriangle style={{ color: '#f59e0b', fontSize: '11px' }} title="Has open maintenance tasks" />
                      )}
                    </div>
                  ) : canEditLineups ? (
                    <select
                      className="input boat-asset-select"
                      value=""
                      onChange={(e) => {
                        updateBoatField(b.id, "speedCoach", e.target.value);
                        flushBoatFieldSave(b.id, "speedCoach", e.target.value);
                      }}
                      style={{ flex: "0 1 auto" }}
                    >
                      <option value="">Select...</option>
                      {boatConfig.speedCoaches.map(s => (
                        <option key={s.id ?? s.value} value={s.value}>{s.value}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>Not set</span>
                  )}
                  {b.speedCoach && canEditLineups && (
                    <button
                      className="btn ghost"
                      style={{ padding: '2px 6px', fontSize: '10px', minHeight: 'auto' }}
                      onClick={() => {
                        updateBoatField(b.id, "speedCoach", "");
                        flushBoatFieldSave(b.id, "speedCoach", "");
                      }}
                      title="Clear speed coach"
                    >
                      ×
                    </button>
                  )}
                </div>
                  {/* Launch — read-only; assign launches from the Launches tab */}
                <div className="boat-asset-row" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', minWidth: 45 }}>Launch:</span>
                  {b.launch ? (
                    <div
                      className="chip"
                      onClick={() => openAssetModal(b.launch, 'launch', `Launch: ${b.launch}`)}
                      style={launchChipStyle}
                      title={`Launch: ${b.launch}${launchStatusInfo ? ` • Status: ${launchStatusInfo.label}` : ''}\nClick to view maintenance tasks`}
                    >
                      {b.launch}
                      {getAssetUsageCount("launch", b.launch) > 1 && (
                        <span className="asset-usage-count">
                          {getAssetUsageCount("launch", b.launch)} boats
                        </span>
                      )}
                      {assetHasOpenTasks(b.launch, 'launch') && (
                        <FaExclamationTriangle style={{ color: '#f59e0b', fontSize: '11px' }} title="Has open maintenance tasks" />
                      )}
                    </div>
                  ) : (
                    <span style={{ fontSize: '11px', color: 'var(--border)', opacity: 0.7 }}>—</span>
                  )}
                </div>
              </div>
            </div>

            {/* Coach assignments */}
            <div
              className="coach-drop-zone"
              onDragOver={!isTouchDevice && canEditLineups ? onDragOver : undefined}
              onDrop={!isTouchDevice && canEditLineups ? (e) => onDropCoachToBoat(e, b.id) : undefined}
              style={{
                padding: '12px 8px',
                margin: '8px 8px 12px',
                backgroundColor: 'var(--bg-elevated)',
                border: isTouchDevice ? '1px solid var(--card-border)' : '2px dashed var(--card-border)',
                borderRadius: '6px',
                minHeight: '74px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                  {canEditLineups ? (isTouchDevice ? "Coaches" : "Coaches (drop here)") : "Coaches"}
                </div>
                {isTouchDevice && canEditLineups && (
                  <button
                    className="btn ghost tiny"
                    style={{ padding: '4px 10px', fontSize: '11px', minHeight: 'auto' }}
                    onClick={() => openCoachPicker(b.id)}
                  >
                    {coachNames.length ? "Edit" : "Assign"}
                  </button>
                )}
              </div>
              {coachNames.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {coachNames.map((coachName, idx) => (
                    <div
                      key={`${coachName}-${idx}`}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: 'var(--card)',
                        border: '1px solid var(--card-border)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: 'var(--text)'
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{coachName}</span>
                      {canEditLineups && (
                        <button
                          className="btn ghost"
                          style={{ padding: '2px 6px', fontSize: '10px', minHeight: 'auto' }}
                          onClick={() => toggleCoachOnBoat(b.id, coachName)}
                          title="Remove coach"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic' }}>
                  {canEditLineups
                    ? (isTouchDevice ? "Tap Assign to select coaches" : "Drag coach here")
                    : "Coach assignments are view only"}
                </div>
              )}
            </div>

            {/* Seats vertical */}
            <div className="seatsCol">
              {(b.seats || []).map((s) => {
                const u = s.userId ? byId.get(s.userId) : null;
                const fillerName = !u && s.guestName ? String(s.guestName).trim() : "";
                const st = u ? attendanceByUser[s.userId] : null;
                const icon = st === "present"
                  ? <FaCheckCircle className="statusIcon present lineup-circle" style={{ fontSize: 14 }} />
                  : st === "absent"
                  ? <AbsentStatusIcon size={14} />
                  : <FaQuestionCircle className="statusIcon pending" style={{ fontSize: 14 }} />;
                return (
                  <div
                    key={s.seat}
                    className={`seat v ${u || fillerName ? "filled" : ""}`}
                    onDragOver={canEditLineups ? onDragOver : undefined}
                    onDrop={canEditLineups ? (e) => onDropToSeat(e, b.id, s.seat) : undefined}
                    onClick={canEditLineups ? () => openSeatPicker(b.id, s.seat, s.userId || null) : undefined}
                    title={
                      u
                        ? `${u.name}${u.position ? " • " + u.position : ""}`
                        : fillerName || s.seat
                    }
                  >
                    <div className="seatLabel">{s.seat}</div>
                    {u ? (
                      <div
                        className={`chip block ${posClass(u.position)}`}
                        draggable={!isTouchDevice && canEditLineups}
                        onDragStart={
                          !isTouchDevice && canEditLineups
                            ? (e) => onDragStartAthlete(e, u, b.id, s.seat)
                            : undefined
                        }
                        style={{
                          ...(getPositionColor(u.position, positionColors) ? {
                            backgroundColor: getPositionColor(u.position, positionColors),
                            borderColor: getPositionColor(u.position, positionColors)
                          } : {}),
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <div className="row seat-chip-row">
                          <div className="seatNum">{s.seat}</div>
                          <div className="seat-details">
                            <div className="seat-name-row">
                              <div className="seat-name-left">
                                <div className="chipName">{u.name}</div>
                              </div>
                              <div className="seat-icon-row">
                                {icon}
                                {(lineupCheckins?.[u.id] === "here" || lineupCheckins?.[u.id] === "not_here") && (
                                  <>
                                    {lineupCheckins?.[u.id] === "here" && (
                                      <span className="checkin-chip clipboard here" title="Checked in">
                                        <FaClipboardCheck aria-label="Checked in" />
                                      </span>
                                    )}
                                    {lineupCheckins?.[u.id] === "not_here" && (
                                      <span className="checkin-chip clipboard not-here" title="Not here">
                                        <FaClipboardQuestion aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                                {canEditLineups && (
                                  <button
                                    className="btn ghost chip-remove"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSeat(b.id, s.seat, null);
                                    }}
                                    title="Remove from seat"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : fillerName ? (
                      <div className="chip block non-roster">
                        <div className="row seat-chip-row">
                          <div className="seatNum">{s.seat}</div>
                          <div className="seat-details">
                            <div className="seat-name-row">
                              <div className="seat-name-left">
                                <div className="chipName">{fillerName}</div>
                                <span className="chipMeta">Guest</span>
                              </div>
                              {canEditLineups && (
                                <button
                                  className="btn ghost chip-remove"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSeat(b.id, s.seat, null, null);
                                  }}
                                  title="Remove from seat"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="empty">
                        {canEditLineups ? "Drop athlete" : "Seat unavailable"}
                      </div>
                    )}
                  </div>
                );
              })}
              {canEditLineups && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <button className="btn ghost small" onClick={() => clearBoatSeats(b)}>Clear</button>
                  <button className="btn ghost small" onClick={() => removeBoat(b.id)}>Delete</button>
                </div>
              )}
            </div>
          </div>
        );
        })}
      </div>
      </div>
      </div>
      {canEditLineups && pickerOpen && (
  <div className="modalBack" onClick={closeSeatPicker} role="dialog" aria-modal="true">
    <div className="modalCard" onClick={(e) => e.stopPropagation()}>
      <button
        className="modal-close"
        aria-label="Close seat picker"
        onClick={closeSeatPicker}
      >
        &times;
      </button>
      <div style={{ marginBottom: 8 }}>
        <strong>Assign athlete</strong>
      </div>

      {pickerCtx?.currentUserId ? (
        <button className="btn ghost small" onClick={clearSeatFromPicker} style={{ marginBottom: 8 }}>
          Unassign current athlete
        </button>
      ) : null}

      <div className="modalFilters">
        <input
          className="input modalSearchInput"
          placeholder="Search name"
          value={pickerSearch}
          onChange={(e) => setPickerSearch(e.target.value)}
        />
        <button
          className={`btn-filter icon-only ${pickerShowFilters ? "active" : ""}`}
          onClick={() => setPickerShowFilters(!pickerShowFilters)}
          title="Filters"
        >
          <FaFilter />
          {[pickerTeam !== "all", pickerSquad !== "all", pickerPosition !== "all", pickerAgeGroup !== "all", pickerAvailability !== "all"].filter(Boolean).length > 0 && (
            <span className="filter-badge">
              {[pickerTeam !== "all", pickerSquad !== "all", pickerPosition !== "all", pickerAgeGroup !== "all", pickerAvailability !== "all"].filter(Boolean).length}
            </span>
          )}
        </button>
        <button
          className={`btn-filter icon-only ${pickerShowFiller ? "active" : ""}`}
          onClick={() => setPickerShowFiller((prev) => !prev)}
          title="Add seat filler"
        >
          <FaUserPlus />
        </button>
      </div>

      {pickerShowFiller && (
        <div className="modalFiltersPanel">
          <input
            className="input modalSearchInput"
            placeholder="Seat filler name"
            value={pickerFillerName}
            onChange={(e) => setPickerFillerName(e.target.value)}
          />
          <button className="btn" onClick={assignFillerFromPicker}>
            Add to seat
          </button>
        </div>
      )}

      {pickerShowFilters && (
        <div className="modalFiltersPanel">
          <select
            className="input filterSelect"
            value={pickerTeam}
            onChange={(e) => setPickerTeam(e.target.value)}
          >
            <option value="all">All Teams</option>
            {pickerTeamOptions.map((team) => (
              <option key={team} value={team}>{team}</option>
            ))}
          </select>
          <select
            className="input filterSelect"
            value={pickerSquad}
            onChange={(e) => setPickerSquad(e.target.value)}
          >
            <option value="all">All Squads</option>
            {pickerSquadOptions.map((squad) => (
              <option key={squad} value={squad}>{squad}</option>
            ))}
          </select>
          <select
            className="input filterSelect"
            value={pickerPosition}
            onChange={(e) => setPickerPosition(e.target.value)}
          >
            <option value="all">All Positions</option>
            {pickerPositionOptions.map((pos) => (
              <option key={pos} value={pos}>{positionLabel(pos)}</option>
            ))}
          </select>
          <select
            className="input filterSelect"
            value={pickerAgeGroup}
            onChange={(e) => setPickerAgeGroup(e.target.value)}
          >
            <option value="all">All Age Groups</option>
            {pickerAgeGroupOptions.map((ag) => (
              <option key={ag} value={ag}>{ag}</option>
            ))}
          </select>
          <select
            className="input filterSelect"
            value={pickerAvailability}
            onChange={(e) => setPickerAvailability(e.target.value)}
          >
            <option value="all">All Availability</option>
            <option value="available">Available</option>
            <option value="unavailable">Unavailable</option>
            <option value="noresponse">No Response</option>
          </select>
          <button
            className="btn-clear-filters"
            onClick={() => {
              setPickerTeam(coachTeam || "all");
              setPickerSquad("all");
              setPickerPosition("all");
              setPickerAgeGroup("all");
              setPickerAvailability("all");
            }}
          >
            Clear all filters
          </button>
        </div>
      )}

      {modalCandidates.length === 0 ? (
        <div className="help">No unassigned athletes available.</div>
      ) : (
        <div className="listCol">
          {modalCandidates.map((r) => {
            const st = attendanceByUser[r.id];
            const icon = st === "present"
              ? <FaCheckCircle className="statusIcon present lineup-circle" />
              : st === "absent"
              ? <AbsentStatusIcon />
              : <FaQuestionCircle className="statusIcon pending" />;
            const customColor = getPositionColor(r.position, positionColors);
            return (
            <button
              key={r.id}
              className={`chip block ${posClass(r.position)}`}
              onClick={() => assignFromPicker(r.id)}
              style={customColor ? {
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: customColor,
                borderColor: customColor
              } : { justifyContent: "space-between", alignItems: "center" }}
            >
              <div className="row" style={{ gap: 8, alignItems: "center" }}>
                {icon}
                <div className="chipName">{r.name}</div>
                {r.position && <div className="chipMeta">{r.position}</div>}
                {r.squad && <div className="chipMeta">{String(r.squad).toUpperCase()}</div>}
                {r.age_group && <div className="chipMeta">{r.age_group}</div>}
              </div>
              
            </button>
          );})}
        </div>
      )}
    </div>
  </div>
)}

      {canEditLineups && boatActionOpen && (
        <div className="modalBack" onClick={closeAddBoatModal} role="dialog" aria-modal="true">
          <div className="modalCard" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" aria-label="Close add boat" onClick={closeAddBoatModal}>
              &times;
            </button>
            <div className="stack" style={{ gap: 12 }}>
              <strong>Select boat size</strong>
              <select
                className="input"
                value={boatActionType}
                onChange={(e) => setBoatActionType(e.target.value)}
              >
                <option value="eight">Eight (8 seats)</option>
                <option value="four">Four (4 seats)</option>
                <option value="pair">Pair (2 seats)</option>
              </select>
              <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
                <button className="btn ghost small" type="button" onClick={closeAddBoatModal}>
                  Cancel
                </button>
                <button
                  className="btn small"
                  type="button"
                  onClick={submitAddBoatModal}
                  disabled={!effectivePracticeId}
                >
                  Add Boat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canEditLineups && templateActionOpen && (
        <div className="modalBack" onClick={closeApplyTemplateModal} role="dialog" aria-modal="true">
          <div className="modalCard" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              aria-label="Close apply template"
              onClick={closeApplyTemplateModal}
            >
              &times;
            </button>
            <div className="stack" style={{ gap: 12 }}>
              <strong>Select template</strong>
              <select
                className="input"
                value={templateActionId}
                onChange={(e) => setTemplateActionId(e.target.value)}
              >
                <option value="">Load template…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.sourceDate ? `(from ${new Date(t.sourceDate).toLocaleDateString()})` : '(shell template)'}
                  </option>
                ))}
              </select>
              <div className="help">
                Applying a template can replace current boats for this practice.
              </div>
              <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
                <button className="btn ghost small" type="button" onClick={closeApplyTemplateModal}>
                  Cancel
                </button>
                <button
                  className="btn small"
                  type="button"
                  onClick={submitApplyTemplateModal}
                  disabled={!templateActionId}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canEditLineups && coachPickerOpen && (
        <div className="modalBack" onClick={closeCoachPicker} role="dialog" aria-modal="true">
          <div className="modalCard coachPickerCard" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              aria-label="Close coach picker"
              onClick={closeCoachPicker}
            >
              &times;
            </button>

            <div style={{ marginBottom: 8 }}>
              <strong>Assign coaches</strong>
              {coachPickerBoat && (
                <div className="help" style={{ marginTop: 4 }}>
                  {coachPickerBoat.name?.trim() ||
                    (coachPickerBoat.type
                      ? `${coachPickerBoat.type.toUpperCase()} • Boat #${coachPickerBoat.id}`
                      : `Boat #${coachPickerBoat.id}`)}
                </div>
              )}
            </div>

            {coachPickerAssignedNames.length > 0 ? (
              <div className="coach-picker-selected">
                {coachPickerAssignedNames.map((name) => (
                  <span key={name} className="tag coach-picker-selected-chip">
                    {name}
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      onClick={() => toggleCoachFromPicker(name)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  className="btn ghost tiny"
                  style={{ marginLeft: "auto" }}
                  onClick={clearCoachAssignmentsFromPicker}
                >
                  Clear all
                </button>
              </div>
            ) : (
              <div className="help" style={{ marginBottom: 12 }}>
                No coaches assigned yet.
              </div>
            )}

            <div className="modalFilters">
              <input
                className="input modalSearchInput"
                placeholder="Search coach"
                value={coachPickerSearch}
                onChange={(e) => setCoachPickerSearch(e.target.value)}
              />
              <select
                className="input filterSelect"
                value={coachPickerStatus}
                onChange={(e) => setCoachPickerStatus(e.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="present">Present</option>
                <option value="pending">Maybe</option>
                <option value="absent">Absent</option>
              </select>
            </div>

            {coachPickerOptions.length === 0 ? (
              <div className="help" style={{ padding: 12, textAlign: "center", background: 'var(--bg-elevated)', borderRadius: 12 }}>
                {coaches.length === 0
                  ? "No coaches available for this practice."
                  : "No coaches match your filters."}
              </div>
            ) : (
              <div className="coach-picker-list">
                {coachPickerOptions.map((coach, idx) => {
                  const assigned = coachPickerAssignedNames.includes(coach.coachName);
                  const statusIcon =
                    coach.status === "present"
                      ? <FaCheckCircle className="statusIcon present lineup-circle" />
                      : coach.status === "absent"
                      ? <AbsentStatusIcon />
                      : <FaQuestionCircle className="statusIcon pending" />;
                  return (
                    <button
                      key={coach.id ?? coach.coachName ?? idx}
                      className={`coach-picker-row ${assigned ? "assigned" : ""}`}
                      onClick={() => toggleCoachFromPicker(coach.coachName)}
                    >
                      <div className="coach-picker-info">
                        {statusIcon}
                        <div>
                          <div className="coach-picker-name">{coach.coachName}</div>
                          <div className="coach-picker-meta">{coachStatusLabel(coach.status)}</div>
                        </div>
                      </div>
                      <span className="coach-picker-pill">
                        {assigned ? "Assigned" : "Add"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Asset Maintenance Tasks Modal */}
      {assetModalOpen && selectedAsset && (
        <div className="modalBack" onClick={closeAssetModal} role="dialog" aria-modal="true">
          <div className="modalCard" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <button
              className="modal-close"
              aria-label="Close asset modal"
              onClick={closeAssetModal}
            >
              &times;
            </button>

            <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <strong>{selectedAsset.label}</strong>
            </div>

            {(selectedAssetStatusInfo || selectedAssetProperties.length > 0 || selectedAssetNotes) && (
              <div
                style={{
                  border: "1px solid var(--card-border)",
                  borderRadius: 12,
                  padding: 12,
                  marginBottom: 16,
                  display: "grid",
                  gap: 12,
                }}
              >
                {selectedAssetStatusInfo && (
                  <span
                    className="tag practice-chip"
                    style={{
                      background: selectedAssetStatusInfo.styles.background,
                      color: selectedAssetStatusInfo.styles.text,
                      borderColor: selectedAssetStatusInfo.styles.border,
                      width: "fit-content",
                    }}
                  >
                    Status: {selectedAssetStatusInfo.label || "No status"}
                  </span>
                )}
                {selectedAssetProperties.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
                    {selectedAssetProperties.map((row) => (
                      <div key={row.label} style={{ fontSize: 12 }}>
                        <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3 }}>
                          {row.label}
                        </div>
                        <div style={{ fontWeight: 600, color: "var(--text-strong)" }}>{row.value}</div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedAssetNotes && (
                  <div style={{ fontSize: 12, color: "var(--text)", background: "var(--bg-elevated)", borderRadius: 8, padding: 10, lineHeight: 1.4 }}>
                    {selectedAssetNotes}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600 }}>
                Open Maintenance Tasks
              </h4>
            </div>

            {assetTasks.length === 0 ? (
              <div className="help" style={{ padding: 16, textAlign: 'center', backgroundColor: 'var(--bg-elevated)', borderRadius: 8 }}>
                No open maintenance tasks for this equipment.
              </div>
            ) : (
              <div className="stack" style={{ gap: 12 }}>
                {assetTasks.map((task) => (
                  <div
                    key={task.id}
                    style={{
                      padding: 12,
                      backgroundColor: 'var(--card)',
                      border: '1px solid var(--card-border)',
                      borderRadius: 8
                    }}
                  >
                    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: 4 }}>
                          {task.equipment_name}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          {task.equipment_type}
                        </div>
                      </div>
                      <span
                        style={{
                          padding: '2px 8px',
                          fontSize: '11px',
                          fontWeight: 600,
                          borderRadius: '4px',
                          backgroundColor:
                            task.status === 'submitted' ? 'rgba(251,191,36,0.15)' :
                            task.status === 'in_progress' ? 'rgba(59,130,246,0.15)' : 'rgba(52,211,153,0.15)',
                          color:
                            task.status === 'submitted' ? '#92400e' :
                            task.status === 'in_progress' ? '#93c5fd' : 'var(--success)'
                        }}
                      >
                        {task.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>

                    <p style={{ margin: '8px 0', fontSize: '13px' }}>
                      {task.description}
                    </p>

                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: 8 }}>
                      Submitted by {task.submitted_by_name} on{' '}
                      {new Date(task.created_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </div>

                    {task.repair_notes && (
                      <div
                        style={{
                          marginTop: 8,
                          padding: 8,
                          backgroundColor: 'var(--bg-elevated)',
                          borderRadius: 4,
                          fontSize: '12px'
                        }}
                      >
                        <strong>Repair Notes:</strong> {task.repair_notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showPublishModal && (
        <PublishLineupModal
          initialNote={publishedNote}
          onClose={() => setShowPublishModal(false)}
          onConfirm={confirmPublish}
        />
      )}
    </div>
  );

}

/* ---- tiny helpers ---- */
function posClass(p) {
  if (!p) return "pos-default";
  const key = String(p).toLowerCase();
  if (key.includes("cox")) return "pos-cox";
  if (key.includes("both") || key.includes("scull")) return "pos-both";
  if (key.includes("port")) return "pos-port";
  if (key.includes("starboard")) return "pos-starboard";
  return "pos-default";
}

function getPositionColor(position, colorMap) {
  if (!position || !colorMap) return null;
  const key = String(position).toLowerCase();
  // Try exact match first
  if (colorMap[key]) return colorMap[key];
  // Try partial matches for common variations
  for (const [name, color] of Object.entries(colorMap)) {
    if (key.includes(name) || name.includes(key)) {
      return color;
    }
  }
  return null;
}

function canonicalPosition(p) {
  if (!p) return "";
  const s = String(p).toLowerCase();
  if (s.includes("cox")) return "cox";
  if (s.includes("starboard") || s.includes("stbd") || s.includes("sb")) return "starboard";
  if (s.includes("port") || s.includes("pt")) return "port";
  if (s.includes("both") || s.includes("scull")) return "both";
  return s;
}

function positionLabel(pos) {
  if (!pos) return "";
  if (pos === "cox") return "Cox";
  if (pos === "starboard") return "Starboard";
  if (pos === "port") return "Port";
  if (pos === "both") return "Both";
  if (pos === "pair") return "Pair";
  return pos;
}

function extractCoachNames(value) {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
}

function coachStatusLabel(status) {
  if (status === "present") return "Present";
  if (status === "absent") return "Absent";
  if (status === "pending") return "Maybe";
  return "Unknown";
}
